// card, cardVisual — single big number with a caption.

import { useEffect, useRef, useState } from "react";
import type { VisualProps } from "./types";
import "./CardVisual.css";

// P3-card: ease-out cubic.
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// P3-card: numeric count-up hook.
//   - Animates from the previously-rendered value to `targetValue` over `duration` ms.
//   - Uses requestAnimationFrame so the render thread drives the tween.
//   - Cleans the in-flight frame on unmount / target change.
//   - Returns the current animated number. Caller is responsible for formatting it.
function useCountUp(targetValue: number, duration = 400): number {
  const [display, setDisplay] = useState<number>(targetValue);
  const fromRef = useRef<number>(targetValue);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // P3-card: cancel any in-flight tween and start fresh from current displayed value.
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    fromRef.current = display;
    startRef.current = null;

    if (fromRef.current === targetValue || !Number.isFinite(targetValue)) {
      setDisplay(targetValue);
      return;
    }

    const step = (ts: number) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const next = fromRef.current + (targetValue - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
        setDisplay(targetValue);
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // P3-card: intentionally exclude `display` so we don't restart on every interim tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetValue, duration]);

  return display;
}

// AGENT-C: PBI-style default formatter for KPI numbers.
//   - Magnitude > 1000 → no decimals, thousands separators.
//   - Magnitude ≤ 1000 → two fraction digits (consistent with PBI's "fewer
//     digits as the number grows" behavior).
//   - Implemented with `Intl.NumberFormat` so locale-grouping is correct.
const _kpiFormatterLarge = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});
const _kpiFormatterSmall = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
function defaultFormat(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) > 1000) return _kpiFormatterLarge.format(n);
  return _kpiFormatterSmall.format(n);
}

// P3-card: try to extract a numeric target. Strings like "$1,234.56" are
// parsed so we can still animate when the backend ships a pre-formatted value.
function asNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const cleaned = v.replace(/[^0-9eE+\-.]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function CardVisual({ spec, data }: VisualProps) {
  // P3-card: pick a numeric target if we have one (animate); otherwise render directly.
  const numericTarget = asNumber(data.value);
  const hasFormatted = typeof data.formatted === "string" && data.formatted.length > 0;
  const caption = spec.title ?? "";

  // Always call the hook (rules of hooks). When the value isn't numeric we
  // pass 0 and ignore the result.
  const animated = useCountUp(numericTarget ?? 0, 400);

  let display: string;
  if (numericTarget == null) {
    // Non-numeric value — fall through to the original static behavior.
    display =
      data.formatted ??
      (data.value !== undefined && data.value !== null ? String(data.value) : "—");
  } else if (hasFormatted) {
    // P3-card: backend supplied a formatter — re-apply the same scale heuristically.
    // Most card formatters in this app are number-locale based; render the
    // animated number with the default formatter while it's tweening, then
    // snap to the exact backend-formatted string at the end of the tween.
    const settled = Math.abs(animated - numericTarget) < 1e-6;
    display = settled ? (data.formatted as string) : defaultFormat(animated);
  } else {
    display = defaultFormat(animated);
  }

  // AGENT-C: caption presence drives a CSS modifier — `.has-caption` shrinks
  // the value from 32px → 24px so the two lines balance visually.
  const hasCaption = caption.length > 0;
  return (
    <div className="card-visual">
      <div
        className={hasCaption ? "card-value has-caption" : "card-value"}
        title={display}
      >
        {display}
      </div>
      {hasCaption && (
        <div className="card-caption" title={caption}>
          {caption}
        </div>
      )}
    </div>
  );
}
